<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  UTM_ATTRIBUTE_KEYS,
  PIXEL_ATTRIBUTE_KEYS,
  SENSITIVE_ATTRIBUTE_KEYS,
} from 'dashboard/components-next/Forms/sourceAttributeKeys';

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const dealAttributes = useMapGetter('attributes/getDealAttributes');

const buildFields = keys =>
  keys
    .map(key =>
      dealAttributes.value.find(attribute => attribute.attributeKey === key)
    )
    .filter(Boolean)
    .map(attribute => ({
      key: attribute.attributeKey,
      label: attribute.attributeDisplayName,
      message: SENSITIVE_ATTRIBUTE_KEYS.includes(attribute.attributeKey)
        ? t('SOURCE_ATTRIBUTES.SENSITIVE_WARNING')
        : '',
    }));

const sections = computed(() => [
  {
    id: 'utm',
    title: t('SOURCE_ATTRIBUTES.UTM_TITLE'),
    fields: buildFields(UTM_ATTRIBUTE_KEYS),
  },
  {
    id: 'pixel',
    title: t('SOURCE_ATTRIBUTES.PIXEL_TITLE'),
    fields: buildFields(PIXEL_ATTRIBUTE_KEYS),
  },
]);

const getFieldValue = key => props.modelValue?.[key] ?? '';

const handleInput = (key, value) => {
  const next = { ...props.modelValue };
  if (value === '' || value === null || value === undefined) {
    delete next[key];
  } else {
    next[key] = value;
  }
  emit('update:modelValue', next);
};
</script>

<template>
  <Accordion
    v-for="section in sections"
    :key="section.id"
    :title="section.title"
    :is-open="false"
  >
    <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
      <Input
        v-for="field in section.fields"
        :key="field.key"
        :model-value="getFieldValue(field.key)"
        :label="field.label"
        :message="field.message"
        custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
        @update:model-value="value => handleInput(field.key, value)"
      />
    </div>
  </Accordion>
</template>
